import React from "react";
import styles from "./styles.module.scss";
import theme from "../../Styles/theme.scss";
import {
  VictoryChart,
  VictoryGroup,
  VictoryBar,
  VictoryAxis,
  VictoryLegend,
} from "victory";
import { AutoSizer } from "react-virtualized";
import { formatPeriodLabel } from "../../Pages/InstallPerformance/utils";
import type { Granularity } from "../../Pages/InstallPerformance/utils";
import type { InstallPeriodType } from "@intelligentlilli/api-layer";

interface InstallPerformanceChartProps {
  periods: InstallPeriodType[];
  granularity: Granularity;
}

const BAR_COLORS = {
  started: theme.secondary3,
  referred: theme.unexpected3,
  failed: theme.risk3,
  succeeded: theme.expected2,
};

const LEGEND_DATA = [
  { name: "Install started", symbol: { fill: BAR_COLORS.started } },
  { name: "In progress", symbol: { fill: BAR_COLORS.referred } },
  { name: "Install failed", symbol: { fill: BAR_COLORS.failed } },
  { name: "Install complete", symbol: { fill: BAR_COLORS.succeeded } },
];

const InstallPerformanceChart = ({
  periods,
  granularity,
}: InstallPerformanceChartProps) => {
  if (!periods || periods.length === 0) {
    return (
      <div className={styles.container}>
        <h2 className={styles.title}>Install performance</h2>
        <div className={styles.empty}>No install activity for this period</div>
      </div>
    );
  }

  const chartData = periods.map((period) => ({
    x: formatPeriodLabel(period, granularity),
    started: period.metrics.startedCount,
    referred: period.metrics.referredCount,
    failed: period.metrics.failedCount,
    succeeded: period.metrics.succeededCount,
  }));

  const maxValue = Math.max(
    ...chartData.flatMap((d) => [d.started, d.referred, d.failed, d.succeeded]),
    1
  );

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>Install performance</h2>
      <div className={styles.chart_wrapper}>
        <AutoSizer disableHeight>
          {({ width }: { width: number }) => {
            const barWidth = Math.max(
              6,
              Math.min(20, Math.floor(width / (periods.length * 6)))
            );
            const chartHeight = 350;

            return (
              <VictoryChart
                width={width}
                height={chartHeight}
                domainPadding={{ x: barWidth * 3 }}
                padding={{ top: 20, bottom: 80, left: 50, right: 20 }}
              >
                <VictoryAxis
                  tickValues={chartData.map((d) => d.x)}
                  style={{
                    axis: { stroke: theme.neutral3 },
                    tickLabels: {
                      fontSize: 11,
                      fill: theme.neutral5,
                      angle: periods.length > 7 ? -45 : 0,
                      textAnchor: periods.length > 7 ? "end" : "middle",
                    },
                    grid: { stroke: "none" },
                  }}
                />
                <VictoryAxis
                  dependentAxis
                  tickFormat={(t: number) =>
                    Number.isInteger(t) ? String(t) : ""
                  }
                  domain={[0, maxValue]}
                  style={{
                    axis: { stroke: theme.neutral3 },
                    tickLabels: { fontSize: 11, fill: theme.neutral5 },
                    grid: {
                      stroke: theme.neutral2,
                      strokeDasharray: "4,4",
                    },
                  }}
                />
                <VictoryGroup offset={barWidth + 2} colorScale={Object.values(BAR_COLORS)}>
                  <VictoryBar
                    data={chartData}
                    x="x"
                    y="started"
                    barWidth={barWidth}
                    cornerRadius={{ top: 2 }}
                  />
                  <VictoryBar
                    data={chartData}
                    x="x"
                    y="referred"
                    barWidth={barWidth}
                    cornerRadius={{ top: 2 }}
                  />
                  <VictoryBar
                    data={chartData}
                    x="x"
                    y="failed"
                    barWidth={barWidth}
                    cornerRadius={{ top: 2 }}
                  />
                  <VictoryBar
                    data={chartData}
                    x="x"
                    y="succeeded"
                    barWidth={barWidth}
                    cornerRadius={{ top: 2 }}
                  />
                </VictoryGroup>
                <VictoryLegend
                  x={0}
                  y={chartHeight - 30}
                  orientation="horizontal"
                  gutter={20}
                  data={LEGEND_DATA}
                  style={{
                    labels: { fontSize: 11, fill: theme.neutral6 },
                  }}
                />
              </VictoryChart>
            );
          }}
        </AutoSizer>
      </div>
    </div>
  );
};

export default InstallPerformanceChart;
